import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, ShieldCheck, MapPin, Handshake, Zap, TrendingDown, Sparkles, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { PROPERTY_TYPES, budgetOptions } from "../lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import PropertyCard from "../components/PropertyCard";
import Seo from "../components/Seo";

const HERO = "https://images.unsplash.com/photo-1779976955613-b74623824d1c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

function Rail({ title, subtitle, icon, params, link }) {
  const { data } = useQuery({
    queryKey: ["rail", params],
    queryFn: async () => (await api.get("/properties", { params: { ...params, limit: 4 } })).data,
  });
  if (!data?.items?.length) return null;
  return (
    <section className="py-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-indigo-800">{icon}<h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{title}</h2></div>
          {subtitle && <p className="text-slate-500 mt-1 text-sm max-w-2xl">{subtitle}</p>}
        </div>
        <Link to={link} className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-indigo-800 hover:gap-2 transition-all">View all <ArrowRight className="w-4 h-4" /></Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {data.items.map((p) => <PropertyCard key={p.id} p={p} />)}
      </div>
    </section>
  );
}

export default function Home() {
  const nav = useNavigate();
  const [loc, setLoc] = useState("");
  const [ptype, setPtype] = useState("");
  const [budget, setBudget] = useState("");
  const { data: sectors } = useQuery({ queryKey: ["home-locations"], queryFn: async () => (await api.get("/locations")).data });

  const search = () => {
    const p = new URLSearchParams();
    if (loc) p.set("q", loc);
    if (ptype) p.set("property_type", ptype);
    if (budget) { const [mn, mx] = budget.split("-"); p.set("min_price", mn); p.set("max_price", mx); }
    nav(`/properties?${p.toString()}`);
  };

  return (
    <div>
      <Seo title="Faridabad Property Vault — Personally Verified Properties" description="Faridabad ki selected properties — personally verified, honestly priced. Handpicked homes, plots and builder floors across Faridabad." />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Faridabad real estate" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/85 to-white/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 md:pt-24 md:pb-24">
          <div className="max-w-3xl animate-fade-up">
            <span className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-800 text-xs font-semibold px-3 py-1.5 rounded-full border border-indigo-200 mb-5">
              <ShieldCheck className="w-3.5 h-3.5" /> Faridabad's Personally Verified Property Platform
            </span>
            <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.1]">
              Faridabad Mein Apni <span className="text-indigo-800">Perfect Property</span> Khojiye
            </h1>
            <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl">
              Personally verified properties. Real photos. Honest pricing. Local property assistance.
            </p>

            {/* Search */}
            <div className="mt-8 bg-white rounded-2xl shadow-xl border border-slate-100 p-3 grid grid-cols-1 md:grid-cols-[1.3fr_1fr_1fr_auto] gap-2" data-testid="hero-search">
              <input value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="Location / Sector" data-testid="search-location"
                className="px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-700/40" />
              <Select value={ptype} onValueChange={setPtype}>
                <SelectTrigger className="rounded-xl h-[46px]" data-testid="search-type"><SelectValue placeholder="Property Type" /></SelectTrigger>
                <SelectContent>{PROPERTY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={budget} onValueChange={setBudget}>
                <SelectTrigger className="rounded-xl h-[46px]" data-testid="search-budget"><SelectValue placeholder="Budget" /></SelectTrigger>
                <SelectContent>{budgetOptions.filter(b => b.value).map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={search} data-testid="search-submit" className="bg-indigo-900 hover:bg-indigo-800 rounded-xl h-[46px] px-6"><Search className="w-4 h-4 mr-2" /> Search</Button>
            </div>

            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-indigo-700" /> Personally Verified Properties</span>
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-indigo-700" /> 100% Faridabad Focused</span>
              <span className="flex items-center gap-1.5"><Handshake className="w-4 h-4 text-indigo-700" /> Site Visit Assistance</span>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Rail title="🔥 Handpicked Properties" subtitle="Hum har property list nahi karte. Sirf selected properties jo genuinely worth considering hain." icon={<Sparkles className="w-6 h-6" />} params={{ featured: true }} link="/properties?featured=true" />

        {/* Sector Explorer */}
        <section className="py-10">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Explore Faridabad</h2>
              <p className="text-slate-500 mt-1 text-sm">Browse verified inventory sector by sector.</p>
            </div>
            <Link to="/locations" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-indigo-800">All sectors <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {(sectors?.items || []).slice(0, 10).map((s) => (
              <Link key={s.id} to={`/locations/${s.slug}`} data-testid={`sector-${s.slug}`}
                className="group relative rounded-2xl overflow-hidden border border-slate-200 aspect-[4/3]">
                <img src={s.image} alt={s.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
                <div className="absolute bottom-2 left-3 right-3 text-white">
                  <div className="font-heading font-bold text-sm">{s.name}</div>
                  <div className="text-[11px] text-white/80">{s.property_count} properties</div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <Rail title="⚡ Urgent Sale" subtitle="Motivated sellers with genuine urgent requirements." icon={<Zap className="w-6 h-6" />} params={{ urgent_sale: true }} link="/urgent-sale" />
        <Rail title="📉 Price Reduced" subtitle="Properties whose price was recently reduced." icon={<TrendingDown className="w-6 h-6" />} params={{ price_reduced: true }} link="/price-reduced" />
        <Rail title="🆕 Just Listed" subtitle="Freshly added verified properties." icon={<Sparkles className="w-6 h-6" />} params={{ new_listing: true }} link="/new-listings" />
      </div>

      {/* Trust band */}
      <section className="bg-indigo-900 text-white mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-3 gap-8">
          {[
            { t: "Verified Inventory", d: "Every property is physically inspected by our team before listing.", i: <ShieldCheck className="w-6 h-6" /> },
            { t: "Honest Pricing", d: "We share our fair-price assessment so you know if it's worth the ask.", i: <TrendingDown className="w-6 h-6" /> },
            { t: "Human Assistance", d: "Real people help you shortlist, visit and evaluate — not a call centre.", i: <Handshake className="w-6 h-6" /> },
          ].map((f) => (
            <div key={f.t} className="flex gap-4">
              <div className="w-12 h-12 rounded-xl bg-indigo-800 flex items-center justify-center shrink-0">{f.i}</div>
              <div><h3 className="font-heading font-bold text-lg">{f.t}</h3><p className="text-indigo-100/80 text-sm mt-1">{f.d}</p></div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
